/** Horizontal module navigation (below the header). One row, never wraps: items beyond the breakpoint budget fold into "More ▾".
 *  Same nav model and permissions as before (constants/osNav.js); Settings lives in the right-hand "Admin" menu. */
import { useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, MoreHorizontal, Settings as SettingsIcon, LayoutGrid } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const isCurrent = (to, pathname, search) => {
  const [base, qs] = to.split("?");
  const on = pathname === base || (base !== "/" && pathname.startsWith(base + "/"));
  if (!on) return false;
  if (!qs) return !new URLSearchParams(search).get("tab");
  const want = new URLSearchParams(qs), have = new URLSearchParams(search);
  return [...want.entries()].every(([k, v]) => have.get(k) === v);
};

function GroupMenu({ g, active, pathname, search, className = "" }) {
  const Icon = g.icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-current={active ? "page" : undefined} data-testid={`os-nav-${g.id}-btn`}
          className={"os-lift h-10 px-3 inline-flex items-center gap-1.5 text-[13px] font-medium border-b-2 -mb-px whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--st-accent)] " +
            (active ? "border-[color:var(--st-accent)] text-[color:var(--st-text)]" : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]") + " " + className}>
          {Icon && <Icon className="h-4 w-4 os-ico" strokeWidth={1.8} aria-hidden="true" />}{g.label}<ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-[70vh] overflow-y-auto" data-testid={`os-nav-${g.id}-menu`}>
        <MenuItems g={g} pathname={pathname} search={search} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuItems({ g, pathname, search }) {
  return (
    <>
      {g.children.map((c) => (
        <DropdownMenuItem key={c.to} asChild>
          <Link to={c.to} aria-current={isCurrent(c.to, pathname, search) ? "page" : undefined} data-testid={`os-subnav-${c.to.split("?")[0].replace(/\//g, "-").replace(/^-/, "")}`}
            className={"text-[13px] " + (isCurrent(c.to, pathname, search) ? "font-semibold text-[color:var(--st-accent)]" : "")}>{c.label}</Link>
        </DropdownMenuItem>
      ))}
      {g.more?.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-[color:var(--st-text-muted)]">Advanced</DropdownMenuLabel>
          {g.more.map((c) => (
            <DropdownMenuItem key={c.to} asChild>
              <Link to={c.to} className={"text-[12.5px] " + (isCurrent(c.to, pathname, search) ? "font-semibold text-[color:var(--st-accent)]" : "text-[color:var(--st-text-muted)]")}>{c.label}</Link>
            </DropdownMenuItem>
          ))}
        </>
      )}
    </>
  );
}

function MoreMenu({ groups, activeGroup, pathname, search, className }) {
  if (!groups.length) return null;
  const active = groups.some((g) => g.id === activeGroup);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="os-nav-more-btn" className={"h-10 px-3 inline-flex items-center gap-1.5 text-[13px] font-medium border-b-2 -mb-px whitespace-nowrap " + (active ? "border-[color:var(--st-accent)] text-[color:var(--st-text)]" : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]") + " " + className}>
          <MoreHorizontal className="h-4 w-4" /> More<ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 max-h-[70vh] overflow-y-auto" data-testid="os-nav-more-menu">
        {groups.map((g, i) => (
          <div key={g.id}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[11px] font-semibold text-[color:var(--st-text)] flex items-center gap-1.5">{g.icon && <g.icon className="h-3.5 w-3.5" />}{g.label}</DropdownMenuLabel>
            {g.children.map((c) => <DropdownMenuItem key={c.to} asChild><Link to={c.to} className="text-[13px]">{c.label}</Link></DropdownMenuItem>)}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ITEM_CLS = "h-10 px-3 inline-flex items-center gap-1.5 text-[13px] font-medium whitespace-nowrap";
const MORE_W = 96;

/** Fold groups into "More" by measured width (any screen, zoom, font or OS), not by fixed breakpoints. */
function useFit(groups) {
  const navRef = useRef(null), measureRef = useRef(null), rightRef = useRef(null);
  const [fit, setFit] = useState(groups.length);
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const calc = () => {
      const widths = [...(measureRef.current?.children || [])].map((el) => el.getBoundingClientRect().width + 2);
      const cs = getComputedStyle(nav);
      const avail = nav.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - (rightRef.current?.getBoundingClientRect().width || 0) - 12;
      let used = 0, n = 0;
      for (; n < widths.length; n++) {
        const reserve = n + 1 < widths.length ? MORE_W : 0;
        if (used + widths[n] + reserve > avail) break;
        used += widths[n];
      }
      setFit(n);
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(nav);
    document.fonts?.ready?.then(calc);
    return () => ro.disconnect();
  }, [groups]);
  return { navRef, measureRef, rightRef, fit };
}

export function ModuleBar({ groups, settings, activeGroup, onAllModules }) {
  const { pathname, search } = useLocation();
  const { navRef, measureRef, rightRef, fit } = useFit(groups);
  return (
    <nav ref={navRef} aria-label="Modules" className="relative hidden md:flex items-center h-10 border-b border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-3 md:px-5 gap-0.5" data-testid="os-modulebar">
      <div aria-hidden="true" className="absolute left-0 top-0 w-0 h-0 overflow-hidden invisible pointer-events-none">
        <div ref={measureRef} className="flex w-max">
          {groups.map((g) => <span key={g.id} className={ITEM_CLS}>{g.icon && <g.icon className="h-4 w-4" />}{g.label}<ChevronDown className="h-3.5 w-3.5" /></span>)}
        </div>
      </div>
      {groups.slice(0, fit).map((g) => <GroupMenu key={g.id} g={g} active={activeGroup === g.id} pathname={pathname} search={search} />)}
      <MoreMenu groups={groups.slice(fit)} activeGroup={activeGroup} pathname={pathname} search={search} className="" />
      <div ref={rightRef} className="ml-auto flex items-center gap-0.5 shrink-0">
        <button onClick={onAllModules} className="inline-flex items-center gap-1.5 h-8 px-2.5 text-[12px] font-medium whitespace-nowrap text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] rounded-md hover:bg-[color:var(--st-surface-alt)]" data-testid="os-all-modules" title="Searchable directory of every module" aria-label="All modules"><LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden="true" /><span className="hidden lg:inline">All modules</span></button>
        {settings.map((g) => (
          <DropdownMenu key={g.id}>
            <DropdownMenuTrigger asChild>
              <button data-testid={`os-nav-${g.id}-btn`} aria-current={activeGroup === g.id ? "page" : undefined}
                className={"h-10 px-3 inline-flex items-center gap-1.5 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px " + (activeGroup === g.id ? "border-[color:var(--st-accent)] text-[color:var(--st-text)]" : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]")}>
                <SettingsIcon className="h-4 w-4" strokeWidth={1.8} /> Admin<ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 max-h-[70vh] overflow-y-auto" data-testid={`os-nav-${g.id}-menu`}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-[color:var(--st-text-muted)]">Settings & administration</DropdownMenuLabel>
              <MenuItems g={g} pathname={pathname} search={search} />
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
      </div>
    </nav>
  );
}
